import {
  Box,
  Button,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React from "react";
import { RightPanel } from "../../components/RightPanel";
import { ManageQuickReplyTemplate } from "./ManageQuickReplyTemplate";
import { IQuickReplyTemplateData } from "../../redux/quickReplyTemplates/quickReplyTemplate.types";
import { ArrowBack, Close, ContentCopyOutlined } from "@mui/icons-material";
import { useDispatchWrapper } from "../../hooks";
import { fetchTemplateModuleSubModuleAsync } from "../../redux/templates/templateActions";
import { showMessage } from "../../redux/messages/messagesActions";
import { ISelectOption } from "../../constants/types";
import { StandardCard } from "../../components/Cards";

interface IManageQuickReplyTemplateRightPanelProps {
  open: boolean;
  templateData?: IQuickReplyTemplateData;
  onClose: () => void;
  onSaveSuccess: (templateData: IQuickReplyTemplateData) => void;
  isFromRecommendedFromRightpanel?: boolean;
  recommendedTemplateDataFromRightpanel?: IQuickReplyTemplateData;
}

export const ManageQuickReplyTemplateRightPanel: React.FC<
  IManageQuickReplyTemplateRightPanelProps
> = (props) => {
  const {
    open,
    templateData,
    onClose,
    onSaveSuccess,
    recommendedTemplateDataFromRightpanel,
    isFromRecommendedFromRightpanel,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatchWrapper();

  const [columnNames, setColumnNames] = React.useState<ISelectOption[]>([]);
  const [columnsLoading, setColumnsLoading] = React.useState(false);

  const saveRef = React.useRef<(() => void) | null>(null);

  const panelHeading = templateData
    ? "Edit Quick Reply Template"
    : "Create Quick Reply Template";

  React.useEffect(() => {
    if (open) {
      setColumnsLoading(true);
      dispatch(
        fetchTemplateModuleSubModuleAsync((modules) => {
          setColumnNames(modules);
          setColumnsLoading(false);
        }, "latest_leads")
      );
    }
  }, [open, dispatch]);

  const copyToClipboard = (columnName: string) => async () => {
    const columnValue = columnName;
    const fieldString = `@@${columnValue}`;
    try {
      await navigator.clipboard.writeText(fieldString);
      dispatch(
        showMessage({
          type: "success",
          displayAs: "snackbar",
          message: "Text copied successfully!",
        })
      );
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const renderColumnsAvailablePanel = () => (
    <StandardCard>
      <Typography
        variant="body1"
        sx={{ p: 2, pb: 0 }}
        fontSize={"1.1rem"}
        color="primary"
        fontWeight={600}
      >
        Columns Available
      </Typography>

      {columnsLoading && (
        <Stack
          p={2}
          height={200}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Typography variant="body1" fontSize={"1rem"} color="textSecondary">
            Loading columns...
          </Typography>
        </Stack>
      )}

      {!columnsLoading && columnNames.length === 0 && (
        <Stack
          p={2}
          height={200}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <Typography variant="body1" fontSize={"1rem"} color="textSecondary">
            No columns available.
          </Typography>
        </Stack>
      )}

      {!columnsLoading && columnNames.length > 0 && (
        <Stack maxHeight={"60vh"} overflow={"auto"} sx={{ p: 1 }}>
          {columnNames.map((column, index) => {
            return (
              <Stack
                key={index}
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                pr={1}
              >
                <ListItemButton
                  onClick={copyToClipboard(column.value as string)}
                >
                  <ListItemText
                    primary={column.label}
                    sx={{
                      "& span": {
                        fontSize: "0.9rem",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        wordWrap: "nowrap",
                      },
                    }}
                  />
                  <ListItemIcon>
                    <ContentCopyOutlined
                      color={"primary"}
                      sx={{ cursor: "pointer" }}
                    />
                  </ListItemIcon>
                </ListItemButton>
              </Stack>
            );
          })}
        </Stack>
      )}
    </StandardCard>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            height: "100dvh",
            borderRadius: 0,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 1,
            py: 1,
          }}
        >
          <IconButton onClick={onClose}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ flex: 1, ml: 1 }}>
            {templateData ? "Edit Template" : "Create Template"}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h3" fontWeight={700}>
              {panelHeading}
            </Typography>
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ManageQuickReplyTemplate
                onSaveSuccess={(templateData) => {
                  onSaveSuccess(templateData);
                }}
                templateData={templateData}
                fromRightPanel
                isFromRecommendedFromRightpanel={isFromRecommendedFromRightpanel}
                recommendedTemplateDataFromRightpanel={
                  recommendedTemplateDataFromRightpanel
                }
                saveRef={saveRef}
              />
            </Grid>
            <Grid item xs={12}>
              {renderColumnsAvailablePanel()}
            </Grid>
          </Grid>
        </Box>
      </Drawer>
    );
  }

  return (
    <>
      <RightPanel
        heading={panelHeading}
        open={open}
        width="80%"
        onClose={onClose}
        actionButtons={() => {
          return (
            <Stack
              direction={"row"}
              justifyContent={"flex-end"}
              spacing={2}
              sx={{ width: "100%", pr: 2 }}
            >
              <Button variant="contained" color="primary" onClick={() => saveRef.current?.()}>
                Save
              </Button>
            </Stack>
          );
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <ManageQuickReplyTemplate
              onSaveSuccess={(templateData) => {
                onSaveSuccess(templateData);
              }}
              templateData={templateData}
              fromRightPanel
              isFromRecommendedFromRightpanel={isFromRecommendedFromRightpanel}
              recommendedTemplateDataFromRightpanel={
                recommendedTemplateDataFromRightpanel
              }
              saveRef={saveRef}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                position: { xs: "static", md: "sticky" },
                top: 16,
              }}
            >
              {renderColumnsAvailablePanel()}
            </Box>
          </Grid>
        </Grid>
      </RightPanel>
    </>
  );
};

